import * as React from "react";
import { InlineRecirc, Placeholder } from "@bloomberg/media-ui-article";

import { INLINE_RECIRC_TAXONOMIES } from "~constants/index";
import { InlineRecircStory, InlineRecircTaxonomy } from "~types/types";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import { Tracker } from "~utils/tracker";

import styles from "./inlineRecirc.module.css";

const MAX_STORIES = 4;

const inlineRecircTracker = new Tracker({
    action: "impression",
    category: "recirc",
    event: "postrImpression",
    label: "postrimpression",
});

export type InlineRecircComponentProps = {
    prefix?: string;
    inlineRecircKey: string;
};

export default function InlineRecircComponent({
    prefix = "",
    inlineRecircKey,
}: InlineRecircComponentProps) {
    const { story } = React.useContext(CurrentStoryContext);

    const [stories, setStories] = React.useState<InlineRecircStory[] | null>(
        null
    );

    const { site, name } = INLINE_RECIRC_TAXONOMIES[inlineRecircKey];
    const sites = [site || name.toLowerCase()];
    const cobrand = story.cobrand;
    const isCobrandEnabled =
        cobrand && INLINE_RECIRC_TAXONOMIES[cobrand]?.cobrandEnabled;

    if (isCobrandEnabled) {
        sites.push(cobrand);
    }

    const fetchInlineRecircStories = async () => {
        const result = await fetch(
            `/article/api/inline-recirc/stories?sites=${sites.join(",")}`
        );

        if (!result.ok) {
            console.log(
                "Error occurred attempting to fetch Inline Recirc stories"
            );
            return null;
        }

        const unfilteredStories: InlineRecircStory[] = await result.json();
        const filteredStories = unfilteredStories
            .filter((inlineRecircStory) => inlineRecircStory.id !== story.id)
            .map((inlineRecircStory, index) => ({
                ...inlineRecircStory,
                url: `${inlineRecircStory.url}?re_source=postr_story_${index}`,
            }));
        return filteredStories;
    };

    React.useEffect(() => {
        fetchInlineRecircStories().then((inlineRecircStories) =>
            setStories(inlineRecircStories)
        );

        inlineRecircTracker.trackEvent();
    }, []);

    const brandData: InlineRecircTaxonomy =
        INLINE_RECIRC_TAXONOMIES[inlineRecircKey];
    const brandDataWithTracking = {
        ...brandData,
        url: `${brandData.url}?re_source=postr_index`,
    };
    const cobrandData = isCobrandEnabled
        ? INLINE_RECIRC_TAXONOMIES[cobrand]
        : undefined;
    const cobrandDataWithTracking = cobrandData && {
        ...cobrandData,
        url: `${cobrandData.url}?re_source=postr_index`,
    };

    return (
        <>
            {stories && stories.length ? (
                <InlineRecirc
                    brand={brandDataWithTracking}
                    className={styles.inlineRecirc}
                    cobrand={cobrandDataWithTracking}
                    prefix={prefix}
                    stories={stories.slice(0, MAX_STORIES)}
                />
            ) : (
                <Placeholder.Image className={styles.inlineRecircPlaceholder} />
            )}
        </>
    );
}
